// Set-password page: redeems the single-use link from a "Set / Reset your
// ChemMasters portal password" email (kanban c369e94b). Public route; the
// ?token= in the link is the credential.
//
// No trackEvent() here on purpose: there is no signed-in user, and the portal
// events flush answers 401, which the api client turns into a redirect to
// /login (seen in local E2E). The audit record for these pages is server-side:
// every request / redemption is a password_reset_tokens row. Controls still
// carry data-action for the shared recorder.

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardBody, Input } from '../components/common';
import {
  passwordResetService,
  resetRefusalFrom,
  type ValidateLinkResponse,
} from '../services/passwordReset';
import styles from './SetPasswordPage.module.css';

const MIN_LENGTH = 8;

type LinkState =
  | { kind: 'checking' }
  | { kind: 'dead'; message: string; reason: string }
  | { kind: 'live'; email: string; name: string | null; purpose: string | null };

export function SetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [link, setLink] = useState<LinkState>({ kind: 'checking' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    passwordResetService
      .validate(token)
      .then((res: ValidateLinkResponse) => {
        if (cancelled) return;
        if (res.valid && res.email) {
          setLink({ kind: 'live', email: res.email, name: res.name ?? null, purpose: res.purpose ?? null });
        } else {
          setLink({
            kind: 'dead',
            reason: res.reason ?? 'invalid',
            message: res.message ?? 'This link is not valid. Ask for a new one from the sign-in page.',
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLink({
          kind: 'dead',
          reason: 'unreachable',
          message: 'We could not check this link right now. Try again in a moment.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (password.length < MIN_LENGTH) {
      setFormError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setFormError('The two passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await passwordResetService.confirm(token, password);
      setDone(res.email);
      setPassword('');
      setConfirm('');
    } catch (err) {
      const refusal = resetRefusalFrom(err);
      setFormError(refusal?.message ?? 'Something went wrong. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = link.kind === 'live' && link.purpose === 'reset' ? 'Reset your password' : 'Set your password';

  return (
    <div className={styles.container}>
      <div className={styles.box}>
        <div className={styles.logoSection}>
          <img src="/chemmasters-logo.png" alt="ChemMasters" className={styles.logo} />
        </div>

        <Card padding="lg">
          <CardBody>
            {done ? (
              <div className={styles.form}>
                <h2 className={styles.title}>Password set</h2>
                <div className={styles.success} role="status">
                  Your password for {done} is set. Sign in with it now.
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  data-action="set_password_go_to_sign_in"
                  onClick={() => navigate('/login', { replace: true })}
                >
                  Go to sign in
                </Button>
              </div>
            ) : link.kind === 'checking' ? (
              <p className={styles.muted} role="status">
                Checking your link…
              </p>
            ) : link.kind === 'dead' ? (
              <div className={styles.form}>
                <h2 className={styles.title}>This link will not work</h2>
                <div className={styles.error} role="alert">
                  {link.message}
                </div>
                <Link to="/forgot-password" className={styles.link} data-action="set_password_request_new_link">
                  Ask for a new link
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className={styles.form} data-section="set_password_form">
                <h2 className={styles.title}>{title}</h2>
                <p className={styles.lead}>
                  Choose a password for <strong>{link.email}</strong>. Only you will know it.
                </p>

                {formError && (
                  <div className={styles.error} role="alert">
                    {formError}
                  </div>
                )}

                {/* Lets password managers bind the new password to the right login. */}
                <input type="hidden" name="username" value={link.email} autoComplete="username" readOnly />

                <Input
                  label="New password"
                  type="password"
                  name="new-password"
                  placeholder={`At least ${MIN_LENGTH} characters`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                />
                <Input
                  label="Confirm new password"
                  type="password"
                  name="confirm-password"
                  placeholder="Type it again"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  isLoading={submitting}
                  data-action="set_password_submit"
                >
                  Save password
                </Button>
              </form>
            )}
          </CardBody>
        </Card>

        <p className={styles.helpText}>
          <Link to="/login" data-action="set_password_back_to_sign_in">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default SetPasswordPage;
